/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

// @ts-ignore
import { Color, IndividualSeriesOptions } from 'highcharts';
import _ from 'underscore';
import {
    IMultiAxisDatasetNode,
    IMultiAxisDatasetVisitor
} from '../../../../store/types/MultiAxisDatasetTypes';
import {
    IAxisItem,
    ILevelsLayout,
    TAxis,
    TGroupedMeasures
} from '../../../../store/types/StateTypes';
import visitMultiAxisDatasetNode from '../../../../store/util/visitMultiAxisDatasetNode';
import formattersFactory from '../../../../formatters/formattersFactory';
import i18n from '../../../../../i18n/AdHocBundle.properties';

export const getValue = (aggregations :string[][], firstIndex :number, secondIndex :number) => {
    let value = 0;
    if (!aggregations[firstIndex]) {
        return 0;
    }
    if (aggregations[firstIndex][secondIndex] !== null) {
        value = parseFloat(aggregations[firstIndex][secondIndex]);
        // eslint-disable-next-line no-self-compare
        if (value !== value) {
            // this is a check for NaN
            value = 0;
        }
    }
    return value;
};

export const getTooltipValue = (props :any, value :any, measure :any = null, currentMeasureId :string = '') :string => {
    const {
        measures,
        defaultFieldAggregationType,
        defaultFieldAggregationFormat
    } = props;

    if (value === null) {
        return 'null';
    }
    if (value === undefined) {
        return 'undefined';
    }

    let fieldAggregationType = defaultFieldAggregationType;
    let fieldAggregationFormat = defaultFieldAggregationFormat;

    if (measure && measure.aggregationType && measure.aggregationFormat) {
        // if the measure is provided, let's use it
        fieldAggregationType = measure.aggregationType;
        fieldAggregationFormat = measure.aggregationFormat;
    } else if (Array.isArray(measures)) {
        if (measures.length === 1) {
            // if there is just one measure, then we have no choice but to use it
            if (measures[0].aggregationType && measures[0].aggregationFormat) {
                fieldAggregationType = measures[0].aggregationType;
                fieldAggregationFormat = measures[0].aggregationFormat;
            }
        } else {
            // seems like we have to find the measure
            let foundMeasure = null;
            for (let i = 0; currentMeasureId && i < measures.length && foundMeasure === null; i += 1) {
                if (measures[i].id === currentMeasureId) {
                    foundMeasure = measures[i];
                }
            }
            if (foundMeasure && foundMeasure.aggregationType && foundMeasure.aggregationFormat) {
                fieldAggregationType = foundMeasure.aggregationType;
                fieldAggregationFormat = foundMeasure.aggregationFormat;
            }
        }
    }

    return formattersFactory(fieldAggregationType).format(value.toString(), fieldAggregationFormat);
};

export const getIdToLabelMap = (staticProperties: any): Map<string, string> => {
    const result = new Map<string, string>();

    const innerBuildLabels = (element: IAxisItem | TGroupedMeasures) => {
        if (element.kind === 'measures') {
            (element as any).measures.forEach((measure: any) => {
                result.set(measure.field, measure.label);
            });
        } else if (element.label) {
            result.set((element as IAxisItem).id, element.label);
        }
    };

    staticProperties.levelsStructure.columns.forEach(innerBuildLabels);
    staticProperties.levelsStructure.rows.forEach(innerBuildLabels);

    return result;
}

export const getMeasures = (levelsStructure : any) => {
    const measureItem = [...levelsStructure.columns, ...levelsStructure.rows]
        .find((item: any) => (item.kind === 'measures'));
    if (!measureItem) {
        return null;
    }
    return measureItem.measures;
};

export const getLabelToFieldMeasureMapping = (levelsStructure: ILevelsLayout) => {
    const measures = getMeasures(levelsStructure);
    const map :any = {};
    if (measures) {
        measures.forEach((measure: any) => {
            map[measure.label] = measure.field;
        });
    }
    return map;
}

export const getFieldToIdMeasureMapping = (levelsStructure: ILevelsLayout) => {
    const measures = getMeasures(levelsStructure);
    const map :any = {};
    if (measures) {
        measures.forEach((measure: any) => {
            map[measure.field] = measure.id;
        });
    }
    return map;
}

export const getFieldToLabelMeasureMapping = (levelsStructure: ILevelsLayout) => {
    const mapping :any = getLabelToFieldMeasureMapping(levelsStructure);
    const reverseMapping :any = {};
    Object.keys(mapping).forEach((key) => {
        reverseMapping[mapping[key]] = key;
    });
    return reverseMapping;
}

export const getSelectedIndex = (items: any[]) => {
    const baseIndex = items.reduce((memo: number, item: any) => (!item.measuresLevel && item.expanded ? memo + 1 : memo), 0);

    let offset = 0;
    for (let i = 1; i <= baseIndex; i += 1) {
        if (items[i - 1].measuresLevel) {
            offset = 1;
        }
    }

    return baseIndex + offset;
}

export function stripChildrenForIndexes(node: IMultiAxisDatasetNode): IMultiAxisDatasetNode {
    let children = [];
    if (!node.total) {
        // if total == null, this means, that the next level is a measure level and they are stored as children
        children = node.children.map((child: any) => ({
            ...child,
            children: []
        }));
    }
    return {
        ...node,
        children
    }
}

export function formatLabel(value: string, levelItem: IAxisItem, useAggregationFormat = true) {
    let res = value;
    if (levelItem && value !== i18n['adhoc.node.total.node']) {
        let { format } = levelItem;
        if (useAggregationFormat && levelItem.aggregationFormat) {
            format = levelItem.aggregationFormat;
        }
        res = formattersFactory(levelItem.type).format(value, format, {
            categorizer: levelItem.categorizer,
            useUserTimezone: true
        });
    }

    return res;
}

export const getPath = (searchedIndex: number, currentAxis: any): string[] | undefined => {
    if (currentAxis === undefined || currentAxis === null) {
        return [];
    }
    if (currentAxis.index === searchedIndex) {
        if (!currentAxis.isTotal) {
            return [currentAxis.label];
        }
        return [];
    }
    let partialResult;
    for (let i = 0; i < currentAxis.children.length && partialResult === undefined; i += 1) {
        const subRow = currentAxis.children[i];
        partialResult = getPath(searchedIndex, subRow);
    }
    if (partialResult) {
        if (currentAxis.label && currentAxis.label.length > 0) {
            partialResult.unshift(currentAxis.label);
        }
        return partialResult;
    }
    if (currentAxis.total) {
        // was not found in the children, check in the total
        const currentAxisTotal = currentAxis.total;
        partialResult = getPath(searchedIndex, currentAxisTotal);
        if (partialResult && currentAxis.label && !currentAxis.isTotal) {
            partialResult.unshift(currentAxis.label);
        }
        return partialResult;
    }
    return undefined;
};

export function getLinkObjectForAxis(index: number, items: any, measuresMapping: any, path: string[] = []): any {
    const res: any = {
        measures: [],
        values: []
    };

    for (let i = 0; i < items.length; i += 1) {
        if (items[i].measuresLevel) {
            // If you put into Columns section in the Data Layout items [Field1, Field2, Measures, Field3]
            // then the 'items' array would directly represent these items in the column.
            // But depending on the value of the Data Layout Slider Position (represented here with 'index' variable)
            // the 'path' array might be:
            // for the Slider with value 4 (yes, 4) 'path' would be [Field1, Field2, Measures, Field3]
            // for the Slider with value 2          'path' would be [Field1, Field2, Measures]
            // for the Slider with value 1          'path' would be [Field1, Measures]
            // for the Slider with value 0          'path' would be [Measures]
            // So, it's not clear where to find the measure name (yes, not measures) in the 'path' array.
            // So, once you go thr 'items' elements you want to know this new position of the measure name
            // and next code allows you to find that position.
            let positionInPath = i;
            const measureOffset = i - index;
            if (measureOffset > 0) {
                positionInPath -= measureOffset;
            }
            const measureName = path[positionInPath];
            res.measures.push(measuresMapping[measureName] ? measuresMapping[measureName] : measureName);
        } else if (i < index) {
            res.values.push({
                key: items[i].hierarchicalName,
                value: path[i]
            })
        }
    }

    return res;
}

function createSerieName(labels: Array<string>) {
    return labels.slice().join(', ');
}

interface IPathSeriesOption extends IndividualSeriesOptions {
    name?: string
    path: string[]
}

export interface ITotalToIndexVisitor {
    array: number[]
}

export const totalToIndexVisitor: IMultiAxisDatasetVisitor<ITotalToIndexVisitor> = {
    preVisitNode(node, ctxt) {
        if (node.index !== -1) {
            ctxt.array.push(node.index);
        }
        return ctxt;
    }
};

interface IColumnSeriesVisitorContext {
    colors: Color[],
    includeMeasureName : boolean,
    selectedColIndex: number,
    levelsStructure?: ILevelsLayout,
    levels?: TAxis,
    array?: Array<{
        colIndex: number,
        measureId?: string,
        series: IPathSeriesOption
    }>,
    counter?: number,
    stack?: Array<IMultiAxisDatasetNode>,
    measuresFromColumns?: any,
    measureFieldToLabelMapping?: any,
    measureFieldToIdMapping?: any,
    inMeasures?: boolean,
    seriesNameGenerator?: Function,
    skipTotals? :boolean
}

export const columnSeriesVisitor: IMultiAxisDatasetVisitor<IColumnSeriesVisitorContext> = {

    preVisit(context) {

        context.array = [];
        context.stack = [];
        context.counter = 0;
        context.inMeasures = false;

        if (context.levelsStructure) {
            if (!context.levels) {
                context.levels = context.levelsStructure.columns;
            }
            if (!context.measuresFromColumns) {
                const measureBlock :any = context.levelsStructure.columns.find((level: any) => (level.kind === 'measures'));
                if (measureBlock) {
                    context.measuresFromColumns = measureBlock.measures;
                }
            }
            if (!context.measureFieldToLabelMapping) {
                context.measureFieldToLabelMapping = getFieldToLabelMeasureMapping(context.levelsStructure);
            }
            if (!context.measureFieldToIdMapping) {
                context.measureFieldToIdMapping = getFieldToIdMeasureMapping(context.levelsStructure);
            }
        }

        return context;
    },

    preVisitNode(node, ctxt, currentDepth) {
        const context = ctxt;

        let { label } = node;
        if (node.isMeasure) {

            // In 'dataset.data.columns' and 'dataset.data.rows' arrays measure's field stored in 'label' property,
            // in other words, instead of accessing 'node.field' we have to access 'node.label' to get measure's field.
            // And the measure node in 'dataset.data.columns' or 'dataset.data.rows' don't have any other properties,
            // like ".id", or ".label". Just ".label". But it has value from "measure.field".
            // Since we want to have a real label of measure we need to use another structure called
            // 'levels', which you may see as variables 'levels.columns' or 'levels.columns'.
            // In these structures the measures have real label value, and field, which allows us to create a map between
            // field and label, which is presented here by variable measureFieldToLabelMapping.
            if (context.measureFieldToLabelMapping && context.measureFieldToLabelMapping[label]) {
                label = context.measureFieldToLabelMapping[label];
            }

        } else if (context.levels && currentDepth <= context.levels.length && !node.isTotal && label) {
            const useAggregationFormat = context.selectedColIndex <= (currentDepth - 1);
            label = formatLabel(label, context.levels[currentDepth - 1] as IAxisItem, useAggregationFormat);
        }

        // @ts-ignore
        context.stack.push({
            ...node,
            modifiedLabel: label
        });

        if (!context.inMeasures && node.isMeasure) {
            context.inMeasures = true;
        }

        return context;
    },

    postVisitNode(node, ctxt, depth) {
        const context = ctxt;

        const seriesNameGenerator = context.seriesNameGenerator || createSerieName;

        if (depth === ctxt.selectedColIndex && !node.isTotal) {

            let includeNode = true;
            if (context.stack && context.skipTotals) {
                for (let i = 0; i < context.stack.length; i += 1) {
                    if (context.stack[i].isTotal) {
                        includeNode = false;
                        break;
                    }
                }
            }

            if (includeNode) {
                const { array: indexes } = visitMultiAxisDatasetNode(
                    stripChildrenForIndexes(node),
                    totalToIndexVisitor,
                    { array: [] }
                );

                // Now, it's time to build the path because we reached selected item on Column axle.
                // And there can be two cases:
                // 1) we have passed measure level (node), which means the measure node is recorded into stack (see preVisitNode above)
                // 2) or we haven't reached measure level (node), which means if there are measures on Columns we have to
                //      take the current path, and extend it with every measure we have (which produces as many paths as
                //      measures we have). If not, then, stick to case number 1

                if (context.inMeasures || !context.measuresFromColumns) {

                    // So, the path already contains measure name, OR, there are no measures in Columns

                    let path: any = [];
                    let measureId = '';
                    // @ts-ignore
                    context.stack.forEach((item) => {
                        const {
                            label,
                            modifiedLabel
                        } = item;

                        if (item.isMeasure) {

                            // we may be lucky and field of the measure (which is presented by 'label' variable here) may
                            // contain measure id, but not always. I mean, they can be equal. But if we have a map between
                            // measure id and measure field, then we might get the real id.
                            // To find out why field is presented by label variable, see the comment text in the beginning
                            // of this preVisitNode function.

                            measureId = label;
                            if (context.measureFieldToIdMapping && context.measureFieldToIdMapping[label]) {
                                measureId = context.measureFieldToIdMapping[label];
                            }

                            if (context.includeMeasureName) {
                                path.push(modifiedLabel);
                            }
                        } else {
                            path.push(modifiedLabel);
                        }
                    });

                    path = path.slice(1);
                    if (path.length === 0) {
                        path.push(i18n['adhoc.node.total.node']);
                    }
                    const serieName = seriesNameGenerator(path);

                    // @ts-ignore
                    context.array.push({
                        colIndex: indexes[0],
                        measureId,
                        series: {
                            name: serieName,
                            path,
                            // @ts-ignore
                            color: context.colors[context.counter % context.colors.length],
                            data: [],
                            yAxis: 0
                        }
                    });

                    // @ts-ignore
                    context.counter += 1;

                } else {

                    // ok, so we have to multiply path as many times as many measures we have because we haven't reached
                    // them (due to the selectedColIndex value).

                    // @ts-ignore
                    const path = context.stack.map((stackNode) => (stackNode.modifiedLabel)).slice(1);

                    context.measuresFromColumns.forEach((measure: any, mIndex: number) => {

                        const pathCopy = [...path];

                        let measureId = '';
                        if (context.includeMeasureName) {
                            measureId = measure.id;
                            // TODO: check it in the report
                            pathCopy.push(measure.label);
                        }

                        const serieName = seriesNameGenerator(pathCopy);

                        // @ts-ignore
                        context.array.push({
                            colIndex: indexes[mIndex],
                            measureId,
                            series: {
                                name: serieName,
                                // @ts-ignore
                                path: pathCopy,
                                // @ts-ignore
                                color: context.colors[context.counter % context.colors.length],
                                data: [],
                                yAxis: 0
                            }
                        });

                        // @ts-ignore
                        context.counter += 1;
                    });
                }
            }
        }

        // @ts-ignore
        ctxt.stack.pop();

        if (context.inMeasures && node.isMeasure) {
            context.inMeasures = false;
        }

        return ctxt;
    }
};

export interface IPieSeriesVisitorContext {
    result: Array<any>,
    stack: Array<IMultiAxisDatasetNode>,
    levels?: IAxisItem[],
    measureFieldToLabelMapping :any,
    measureFieldToIndexMap? :any,
    currentMeasureIndex?: number,
    selectedIndex: number,
    skipTotals: boolean,
    includeEmptyLabels? :boolean,
    includeMeasureName: boolean
}

export const pieSeriesVisitor: IMultiAxisDatasetVisitor<IPieSeriesVisitorContext> = {

    preVisit(context) {
        context.currentMeasureIndex = 0;
        return context;
    },

    preVisitNode(node, ctx, depth) {
        const context = ctx;

        let { label } = node;
        if (node.isMeasure) {

            // In 'dataset.data.columns' and 'dataset.data.rows' arrays measure's field stored in 'label' property,
            // in other words, instead of accessing 'node.field' we have to access 'node.label' to get measure's field.
            // And the measure node in 'dataset.data.columns' or 'dataset.data.rows' don't have any other properties,
            // like ".id", or ".label". Just ".label". But it has value from "measure.field".
            // Since we want to have a real label of measure we need to use another structure called
            // 'levels', which you may see as variables 'levels.columns' or 'levels.columns'.
            // In these structures the measures have real label value, and field, which allows us to create a map between
            // field and label, which is presented here by variable measureFieldToLabelMapping.

            // The same applies to 'measureFieldToIndexMap' mapping: it helps us to understand what position particular
            // measures takes in the Layout Band (in case we have multiply measures).

            if (context.measureFieldToIndexMap && context.measureFieldToIndexMap[label]) {
                context.currentMeasureIndex = context.measureFieldToIndexMap[label];
            }

            if (context.measureFieldToLabelMapping[label]) {
                label = context.measureFieldToLabelMapping[label];
            }
            if (!context.includeMeasureName) {
                label = '';
            }

        } else if (context.levels && depth <= context.levels.length && !node.isTotal && label) {
            const useAggregationFormat = context.selectedIndex <= (depth - 1);
            label = formatLabel(label, context.levels[depth - 1], useAggregationFormat);
        }

        context.stack.push({
            ...node,
            modifiedLabel: label
        });

        return context;
    },

    postVisitNode(node, ctx, depth) {
        const context = ctx;

        if (depth === context.selectedIndex && !node.isTotal) {

            let includeNode = true;
            if (context.skipTotals) {
                for (let i = 0; i < context.stack.length; i += 1) {
                    if (context.stack[i].isTotal) {
                        includeNode = false;
                        break;
                    }
                }
            }

            if (includeNode) {
                const { array: indexes } = visitMultiAxisDatasetNode(
                    stripChildrenForIndexes(node),
                    totalToIndexVisitor,
                    { array: [] }
                );
                const index = indexes[0];

                const title = context.stack[context.selectedIndex].modifiedLabel;

                const itemsFromStack = context.stack.slice(1)
                    // includeEmptyLabels allows empty labels be included into path
                    .filter((stackNode) => (context.includeEmptyLabels || stackNode.modifiedLabel));

                const pathByFields = itemsFromStack
                    .filter((stackNode) => (!stackNode.isMeasure))
                    .map((stackNode) => (stackNode.modifiedLabel));

                const pathByFieldsAndMeasures = itemsFromStack
                    .map((stackNode) => (stackNode.modifiedLabel));

                context.result.push({
                    index,
                    indexes: [...indexes],
                    title,
                    measureIndex: context.currentMeasureIndex,
                    pathByFields,
                    pathByFieldsAndMeasures
                });
            }
        }

        context.stack.pop();

        return context;
    }
};

export interface IRowsToArrayVisitor {
    array: IMultiAxisDatasetNode[],
    indexes: number[],
    stack: Array<IMultiAxisDatasetNode>,
    selectedRowIndex: number,
    skipTotals? :boolean
}

export const rowsToArrayVisitor: IMultiAxisDatasetVisitor<IRowsToArrayVisitor> = {
    preVisitNode(node, context, currentDepth) {
        if (currentDepth === context.selectedRowIndex && !node.isTotal) {

            let includeNode = true;
            if (context.stack && context.skipTotals) {
                for (let i = 0; i < context.stack.length; i += 1) {
                    if (context.stack[i].isTotal) {
                        includeNode = false;
                        break;
                    }
                }
            }

            if (includeNode) {
                const strippedNode = stripChildrenForIndexes(node);
                context.array.push(strippedNode);
            }
        }

        context.stack.push(node);

        return context;
    },

    postVisitNode(node, ctx) {
        const context = ctx;
        context.stack.pop();
        return context;
    },

    postVisit(context) {
        const indexArray :any[] = context.array.map((node) => (
            visitMultiAxisDatasetNode(node, totalToIndexVisitor, { array: [] }).array
        ));

        return {
            ...context,
            indexes: _.flatten(indexArray)
        }
    }
};

export interface IRowsToMeasureIdsVisitor {
    measureIds: any[],
    selectedRowIndex: number,

    // private fields to track our paths while we are traversing thr the nodes
    metTheMeasureNode? :boolean,
    metTheDataNode? :boolean,
    currentMeasure? :any
}

export const rowsToMeasureIdsVisitor: IMultiAxisDatasetVisitor<IRowsToMeasureIdsVisitor> = {

    preVisit(context) {

        // private fields to track our paths while we are traversing thr the nodes
        context.metTheMeasureNode = false;
        context.metTheDataNode = false;
        context.currentMeasure = null;

        return context;
    },

    preVisitNode(node, context, currentDepth) {
        if (node.isMeasure) {
            // passing the measure node
            context.metTheMeasureNode = true;
            context.currentMeasure = node;
        }
        if (currentDepth === context.selectedRowIndex && !node.isTotal) {
            // passing the data node
            context.metTheDataNode = true;
        }
        if (context.metTheMeasureNode && context.metTheDataNode) {
            context.measureIds.push(context.currentMeasure.label);
        }
        return context;
    },

    postVisitNode(node, context, currentDepth) {
        if (node.isMeasure) {
            // leaving the measure node behind
            context.metTheMeasureNode = false;
            context.currentMeasure = null;
        }
        if (currentDepth === context.selectedRowIndex && !node.isTotal) {
            context.metTheDataNode = false;
        }
        return context;
    }
};

export interface IHeatMapRowsToArrayVisitor {
    array: IMultiAxisDatasetNode[],
    indexes: number[],
    selectedRowIndex: number
}

export const heatMapRowsToArrayVisitor: IMultiAxisDatasetVisitor<IHeatMapRowsToArrayVisitor> = {
    preVisitNode(node, context, currentDepth) {
        if (currentDepth === context.selectedRowIndex && !node.isTotal) {
            context.array.push(stripChildrenForIndexes(node));
        }

        return context;
    },

    postVisit(context) {
        const indexArray :any[] = context.array.map((node) => (
            visitMultiAxisDatasetNode(node, totalToIndexVisitor, { array: [] }).array
        ));

        if (context.array[0]) {
            if (context.array[0].index === -1 && context.array[0].total) {
                context.array = [context.array[0].total as IMultiAxisDatasetNode];
            }
        }

        return {
            ...context,
            indexes: _.flatten(indexArray)
        }
    }
};

const findInDatasetByIndex = (indexToFind :number, itemToCheck :IMultiAxisDatasetNode, findMeasure :boolean = true) => {
    if (itemToCheck.index === indexToFind) {
        if (findMeasure) {
            if (itemToCheck.isMeasure) {
                return itemToCheck;
            }
        } else {
            return itemToCheck;
        }
    }

    if (itemToCheck.total) {
        const found :any = findInDatasetByIndex(indexToFind, itemToCheck.total, findMeasure);
        if (found) {
            return found;
        }
    }

    if (itemToCheck.children) {
        for (let i = 0; i < itemToCheck.children.length; i += 1) {
            const found :any = findInDatasetByIndex(indexToFind, itemToCheck.children[i], findMeasure);
            if (found) {
                return found;
            }
        }
    }

    return null;
};

const findInLevelsById = (idToFind :string, levels :any) => {

    for (let i = 0; i < levels.length; i += 1) {
        const itemToCheck = levels[i];

        if (itemToCheck.kind === 'measures') {
            const found :any = findInLevelsById(idToFind, itemToCheck.measures);
            if (found) {
                return found;
            }
        } else if (itemToCheck.id === idToFind) {
            return itemToCheck;
        }
    }

    return null;
};

export function findLayoutItemByIndex(indexToFind :number, data :any, levelsStructure :any) :any {

    let foundDataSetItem :any = findInDatasetByIndex(indexToFind, data.columns);
    if (!foundDataSetItem) {
        foundDataSetItem = findInDatasetByIndex(indexToFind, data.rows);
        if (!foundDataSetItem) {
            foundDataSetItem = findInDatasetByIndex(indexToFind, data.columns, false);
            if (!foundDataSetItem) {
                foundDataSetItem = findInDatasetByIndex(indexToFind, data.rows, false);
            }
        }
    }
    if (!foundDataSetItem) {
        return null;
    }

    let foundLevelItem :any = findInLevelsById(foundDataSetItem.label, levelsStructure.columns);
    if (!foundLevelItem) {
        foundLevelItem = findInLevelsById(foundDataSetItem.label, levelsStructure.rows);
    }
    return foundLevelItem;
}

export function findLayoutItemById(labelToFind :string, levelsStructure :any) :any {

    let foundLevelItem :any = findInLevelsById(labelToFind, levelsStructure.columns);
    if (!foundLevelItem) {
        foundLevelItem = findInLevelsById(labelToFind, levelsStructure.rows);
    }
    return foundLevelItem;
}
